/**
 * Math behind the free /tools pages. Pure functions so the pages stay thin and the
 * rules are unit-tested.
 */

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** Parses "$12.03", "12.03" or "1,203.06" into dollars; null when it isn't a price. */
export function parsePrice(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

// ---- Home Depot price decoder ----

export type HDPriceCode = "penny" | "final" | "early" | "regular";

export interface HDPriceDecoding {
  price: number;
  cents: string;
  code: HDPriceCode;
  headline: string;
  detail: string;
  /** Percent off the original price, when one was given and is higher. */
  percentOff?: number;
  savings?: number;
}

export function decodeHomeDepotPrice(price: number, originalPrice?: number | null): HDPriceDecoding {
  const cents = Math.round(price * 100) % 100;
  const centsLabel = `.${String(cents).padStart(2, "0")}`;
  let code: HDPriceCode = "regular";
  let headline = "Not a clearance code";
  let detail = `Prices ending in ${centsLabel} aren't among the clearance endings shoppers report for Home Depot. It's most likely the regular or sale price.`;
  if (price <= 0.01) {
    code = "penny";
    headline = "Penny item";
    detail = "An item ringing up at $0.01 has been flagged for removal from the floor. It won't be tagged at a penny, and the store can decline to sell it.";
  } else if (cents === 3) {
    code = "final";
    headline = "Final markdown";
    detail = "A price ending in .03 is commonly reported as the last markdown before the item is pulled — the best penny candidate in the aisle. Check the tag date: the longer it's sat at .03, the closer it is.";
  } else if (cents === 6) {
    code = "early";
    headline = "Early clearance markdown";
    detail = "A price ending in .06 is commonly reported as an earlier clearance markdown. Expect at least one more drop, often to a .03 price, before it's removed.";
  }
  const result: HDPriceDecoding = { price, cents: centsLabel, code, headline, detail };
  if (originalPrice && originalPrice > price) {
    result.savings = round2(originalPrice - price);
    result.percentOff = Math.round((1 - price / originalPrice) * 100);
  }
  return result;
}

// ---- Target clearance decoder ----

export const TARGET_MARKDOWN_STEPS = [15, 30, 50, 70, 90] as const;

export const TARGET_DEPARTMENT_DAYS: { department: string; day: string }[] = [
  { department: "Electronics & entertainment", day: "Monday" },
  { department: "Baby", day: "Monday" },
  { department: "Kitchen", day: "Monday" },
  { department: "Stationery & books", day: "Monday" },
  { department: "Grocery / market", day: "Monday or Tuesday" },
  { department: "Women's clothing", day: "Tuesday" },
  { department: "Domestics (bedding, towels)", day: "Tuesday" },
  { department: "Pets", day: "Tuesday" },
  { department: "Men's & kids' clothing", day: "Wednesday" },
  { department: "Health & beauty", day: "Wednesday" },
  { department: "Shoes", day: "Wednesday" },
  { department: "Diapers", day: "Wednesday" },
  { department: "Home décor & housewares", day: "Thursday" },
  { department: "Toys", day: "Thursday" },
  { department: "Sporting goods", day: "Thursday" },
  { department: "Lingerie & luggage", day: "Thursday" },
  { department: "Cosmetics", day: "Thursday" },
  { department: "Automotive & hardware", day: "Friday" },
  { department: "Bath", day: "Friday" },
  { department: "Jewelry & accessories", day: "Friday" },
];

export interface TargetDecoding {
  currentPrice: number;
  savings: number;
  nextSteps: { percent: number; price: number; extraSavings: number }[];
  /** "final" when the price ends in 4 (commonly reported last markdown). */
  endingSignal: "final" | "may-drop" | null;
}

/** Target clearance math. `stickerPrice` (what's printed) wins over the computed price. */
export function decodeTargetClearance(originalPrice: number, percentOff: number, stickerPrice?: number | null): TargetDecoding {
  const computed = round2(originalPrice * (1 - percentOff / 100));
  const currentPrice = stickerPrice && stickerPrice > 0 ? stickerPrice : computed;
  const lastDigit = Math.round(currentPrice * 100) % 10;
  return {
    currentPrice,
    savings: round2(Math.max(0, originalPrice - currentPrice)),
    nextSteps: TARGET_MARKDOWN_STEPS.filter((p) => p > percentOff).map((percent) => {
      const price = round2(originalPrice * (1 - percent / 100));
      return { percent, price, extraSavings: round2(Math.max(0, currentPrice - price)) };
    }),
    endingSignal: stickerPrice && stickerPrice > 0 ? (lastDigit === 4 ? "final" : "may-drop") : null,
  };
}

// ---- Dollar General coupon calculator ----

export interface DGBasketItem {
  id: string;
  name: string;
  price: number;
  qty: number;
  couponType: "none" | "amount" | "percent";
  /** Dollars for "amount", percent for "percent". Applied once per line. */
  couponValue: number;
}

export interface DGBasketOptions {
  thresholdEnabled: boolean;
  thresholdSpend: number;
  thresholdOff: number;
  /** "after": the spend counts after item coupons (DG's usual fine print). */
  thresholdBasis: "after" | "before";
  taxRate: number;
  cashBack: number;
}

export interface DGBasketLine {
  id: string;
  lineTotal: number;
  coupon: number;
  /** Line cost after its coupon and its share of the threshold coupon, pre-tax. */
  netEach: number;
}

export interface DGBasketResult {
  subtotal: number;
  itemCoupons: number;
  afterItemCoupons: number;
  thresholdApplied: boolean;
  thresholdDiscount: number;
  shortBy: number;
  preTax: number;
  tax: number;
  outOfPocket: number;
  netAfterCashBack: number;
  totalSavings: number;
  lines: DGBasketLine[];
}

export function calculateDGBasket(items: DGBasketItem[], options: DGBasketOptions): DGBasketResult {
  const lines = items.map((item) => {
    const qty = Math.max(0, Math.floor(item.qty || 0));
    const lineTotal = round2(Math.max(0, item.price) * qty);
    let coupon = 0;
    if (qty > 0 && item.couponType === "amount") coupon = Math.max(0, item.couponValue);
    if (qty > 0 && item.couponType === "percent") coupon = round2(item.price * Math.min(100, Math.max(0, item.couponValue)) / 100);
    coupon = round2(Math.min(coupon, lineTotal));
    return { id: item.id, qty, lineTotal, coupon };
  });
  const subtotal = round2(lines.reduce((s, l) => s + l.lineTotal, 0));
  const itemCoupons = round2(lines.reduce((s, l) => s + l.coupon, 0));
  const afterItemCoupons = round2(subtotal - itemCoupons);
  const qualifying = options.thresholdBasis === "after" ? afterItemCoupons : subtotal;
  const thresholdApplied = options.thresholdEnabled && options.thresholdSpend > 0 && qualifying >= options.thresholdSpend;
  const thresholdDiscount = thresholdApplied ? round2(Math.min(options.thresholdOff, afterItemCoupons)) : 0;
  const shortBy = options.thresholdEnabled && !thresholdApplied ? round2(Math.max(0, options.thresholdSpend - qualifying)) : 0;
  const preTax = round2(afterItemCoupons - thresholdDiscount);
  const tax = round2(preTax * Math.max(0, options.taxRate) / 100);
  const outOfPocket = round2(preTax + tax);
  const netAfterCashBack = round2(outOfPocket - Math.max(0, options.cashBack));
  return {
    subtotal,
    itemCoupons,
    afterItemCoupons,
    thresholdApplied,
    thresholdDiscount,
    shortBy,
    preTax,
    tax,
    outOfPocket,
    netAfterCashBack,
    totalSavings: round2(itemCoupons + thresholdDiscount),
    lines: lines.map((l) => {
      const afterCoupon = l.lineTotal - l.coupon;
      // The threshold coupon is shared in proportion to what each line still costs.
      const share = afterItemCoupons > 0 ? (afterCoupon / afterItemCoupons) * thresholdDiscount : 0;
      return { id: l.id, lineTotal: l.lineTotal, coupon: l.coupon, netEach: l.qty > 0 ? round2((afterCoupon - share) / l.qty) : 0 };
    }),
  };
}

// ---- Resale profit calculator ----

export interface MarketplaceFee {
  id: string;
  label: string;
  percent: number;
  fixed: number;
  /** Fee is charged on shipping the buyer pays, too. */
  onShipping: boolean;
  minFee?: number;
  /** Sales under this amount pay `flatUnderFee` instead of the percentage. */
  flatUnder?: number;
  flatUnderFee?: number;
  /** Fixed fee when the sale is at or under $10 (eBay's smaller per-order fee). */
  smallOrderFixed?: number;
  note?: string;
}

export const MARKETPLACE_FEES: MarketplaceFee[] = [
  { id: "ebay", label: "eBay", percent: 13.6, fixed: 0.4, smallOrderFixed: 0.3, onShipping: true, note: "Most categories; per-order fee is $0.30 at $10 and under." },
  { id: "amazon", label: "Amazon (referral)", percent: 15, fixed: 0, onShipping: true, note: "Referral fee for most categories. Add FBA fulfillment fees under other costs." },
  { id: "mercari", label: "Mercari", percent: 12.9, fixed: 0.5, onShipping: false, note: "Selling fee plus payment processing." },
  { id: "facebook", label: "Facebook (shipped)", percent: 10, fixed: 0, minFee: 0.8, onShipping: false, note: "Shipped orders; local pickup is free." },
  { id: "facebook-local", label: "Facebook (local pickup)", percent: 0, fixed: 0, onShipping: false },
  { id: "poshmark", label: "Poshmark", percent: 20, fixed: 0, flatUnder: 15, flatUnderFee: 2.95, onShipping: false, note: "Buyer pays shipping; flat fee under $15." },
  { id: "whatnot", label: "Whatnot", percent: 10.9, fixed: 0.3, onShipping: true, note: "Commission plus payment processing." },
  { id: "custom", label: "Custom", percent: 0, fixed: 0, onShipping: false },
];

export interface ResaleInputs {
  buyPrice: number;
  taxRate: number;
  salePrice: number;
  shippingCharged: number;
  shippingCost: number;
  otherCosts: number;
  fee: MarketplaceFee;
}

export interface ResaleResult {
  cost: number;
  fees: number;
  profit: number;
  margin: number | null;
  roi: number | null;
  breakEven: number;
}

export function marketplaceFee(fee: MarketplaceFee, salePrice: number, shippingCharged: number): number {
  if (salePrice <= 0) return 0;
  if (fee.flatUnder && fee.flatUnderFee !== undefined && salePrice < fee.flatUnder) return fee.flatUnderFee;
  const base = salePrice + (fee.onShipping ? shippingCharged : 0);
  const fixed = fee.smallOrderFixed !== undefined && base <= 10 ? fee.smallOrderFixed : fee.fixed;
  const amount = (base * fee.percent) / 100 + fixed;
  return round2(Math.max(amount, fee.minFee ?? 0));
}

export function calculateResale(input: ResaleInputs): ResaleResult {
  const cost = round2(input.buyPrice * (1 + Math.max(0, input.taxRate) / 100));
  const profitAt = (sale: number) =>
    sale + input.shippingCharged - marketplaceFee(input.fee, sale, input.shippingCharged) - input.shippingCost - input.otherCosts - cost;
  const fees = marketplaceFee(input.fee, input.salePrice, input.shippingCharged);
  const profit = round2(profitAt(input.salePrice));
  // Profit only rises with the sale price, so bisect for the zero.
  let lo = 0;
  let hi = 1_000_000;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (profitAt(mid) >= 0) hi = mid;
    else lo = mid;
  }
  const revenue = input.salePrice + input.shippingCharged;
  return {
    cost,
    fees,
    profit,
    margin: revenue > 0 ? Math.round((profit / revenue) * 1000) / 10 : null,
    roi: cost > 0 ? Math.round((profit / cost) * 1000) / 10 : null,
    breakEven: Math.ceil(hi * 100) / 100,
  };
}
